const CART_LINE_ITEM_SOURCE = {
  crossSell: 'cross sell product',
  upsell: 'upsell product',
};

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');

  const formatString = format || window.Shopify?.money_format || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  };

  const formatType = formatString.match(placeholderRegex)?.[1] || 'amount';
  let value = '';

  switch (formatType) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

function appendLineItemProperties(formData, properties, itemIndex = null) {
  if (!properties) return;

  Object.entries(properties).forEach(([key, value]) => {
    if (itemIndex === null) {
      formData.append(`properties[${key}]`, value);
    } else {
      formData.append(`items[${itemIndex}][properties][${key}]`, value);
    }
  });
}

if (!customElements.get('product-purchase-row')) {
  customElements.define(
    'product-purchase-row',
    class ProductPurchaseRow extends HTMLElement {
      connectedCallback() {
        const card = this.closest('.product__info-card');
        if (!card || card.querySelector('.product__purchase-row')) return;

        const qty = card.querySelector('.product-form__quantity');
        const form = card.querySelector('.product-form:not(.product-form--upsell):not(.product-form--cross-sell)');
        const formWrapper = form?.parentElement;
        if (!qty || !formWrapper) return;

        const row = document.createElement('div');
        row.className = 'product__purchase-row';
        qty.parentElement.insertBefore(row, qty);
        row.appendChild(qty);
        row.appendChild(formWrapper);
      }
    }
  );
}

if (!customElements.get('cross-sell-variants')) {
  customElements.define(
    'cross-sell-variants',
    class CrossSellVariants extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-cross-sell-variants]').textContent);
        this.mediaMap = JSON.parse(this.querySelector('[data-cross-sell-media]').textContent);
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.priceId = this.dataset.priceId || `CrossSellPrice-${this.sectionId}`;
        this.mediaId = this.dataset.mediaId || `CrossSellMedia-${this.sectionId}`;
        this.productCard = this.closest('[data-cross-sell-product-card]');
        this.hiddenVariantInput = this.productCard?.querySelector('[data-cross-sell-variant-id]');
        this.isCrossSellCard = this.dataset.crossSellCard === 'true';
        this.saveTagType = this.dataset.saveTagType || window.themeVariantSaveTagType || 'price';

        this.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', this.onOptionChange.bind(this));
        });

        this.updateOptionAvailability();
        this.updateSaveTags();

        if (this.isCrossSellCard) {
          this.syncCurrentVariant();
        } else if (this.formId) {
          this.syncFormVariant();
        }
      }

      syncFormVariant() {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariant(selectedOptions);
        if (!variant) return;

        const form = document.getElementById(this.formId);
        const variantInput = form?.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = !variant.available;
        }

        const submitBtn = form?.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = !variant.available;

        this.updatePrice(variant);
        this.updateMedia(variant.id);
      }

      onOptionChange() {
        this.updateOptionAvailability();
        this.updateSaveTags();

        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariant(selectedOptions);
        if (!variant) return;

        if (this.isCrossSellCard) {
          this.syncVariant(variant);
          return;
        }

        this.syncFormVariant();
      }

      isOptionValueAvailable(optionPosition, value, selectedOptions) {
        const index = optionPosition - 1;
        return this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[index] !== value) return false;
          return variant.options.every((opt, i) => {
            if (i === index) return true;
            if (!selectedOptions[i]) return true;
            return opt === selectedOptions[i];
          });
        });
      }

      updateOptionAvailability() {
        const selectedOptions = this.getSelectedOptions();

        this.querySelectorAll('fieldset').forEach((fieldset) => {
          let checkedUnavailable = false;

          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const optionPosition = parseInt(input.dataset.optionPosition, 10);
            const isAvailable = this.isOptionValueAvailable(optionPosition, input.value, selectedOptions);

            input.disabled = !isAvailable;
            input.classList.toggle('disabled', !isAvailable);

            if (input.checked && !isAvailable) checkedUnavailable = true;
          });

          if (checkedUnavailable) {
            const firstAvailable = fieldset.querySelector('input[type="radio"]:not(:disabled)');
            if (firstAvailable) {
              firstAvailable.checked = true;
              firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }

      syncCurrentVariant() {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariant(selectedOptions) || this.variants[0];
        if (variant) this.syncVariant(variant);
      }

      syncVariant(variant) {
        if (this.hiddenVariantInput) {
          this.hiddenVariantInput.value = variant.id;
          this.hiddenVariantInput.disabled = !variant.available;
        }

        if (this.productCard) {
          this.productCard.dataset.variantPrice = variant.price;
          if (this.productCard.dataset.selected === 'true' && !variant.available) {
            this.deselectProductCard(this.productCard);
          }

          const selectBtn = this.productCard.querySelector('[data-cross-sell-select]');
          const selectMedia = this.productCard.querySelector('[data-cross-sell-select-media]');
          if (selectBtn) selectBtn.disabled = !variant.available;
          if (selectMedia) {
            selectMedia.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
            if (!variant.available) selectMedia.setAttribute('tabindex', '-1');
            else selectMedia.setAttribute('tabindex', '0');
          }
        }

        this.updatePrice(variant);
        this.updateMedia(variant.id);
        this.dispatchCrossSellUpdate();
      }

      dispatchCrossSellUpdate() {
        this.closest('.cross-sell-modal')?.dispatchEvent(new CustomEvent('cross-sell:update', { bubbles: true }));
      }

      deselectProductCard(card) {
        const selectBtn = card.querySelector('[data-cross-sell-select]');
        const label = selectBtn?.querySelector('[data-cross-sell-select-label]');

        card.dataset.selected = 'false';
        card.classList.remove('cross-sell-modal__product--selected');

        if (selectBtn) {
          selectBtn.classList.remove('cross-sell-modal__select-btn--selected');
          selectBtn.setAttribute('aria-pressed', 'false');
        }

        if (label) label.textContent = 'Select';

        this.dispatchCrossSellUpdate();
      }

      getSelectedOptions() {
        const options = [];
        this.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) options[parseInt(checked.dataset.optionPosition, 10) - 1] = checked.value;
        });
        return options;
      }

      findVariant(selectedOptions) {
        return this.variants.find((variant) => {
          return variant.options.every((opt, i) => opt === selectedOptions[i]);
        });
      }

      findVariantForOptionValue(optionPosition, value, selectedOptions) {
        const index = optionPosition - 1;
        const options = [...selectedOptions];
        options[index] = value;

        return this.findVariant(options);
      }

      formatSaveTagText(variant) {
        if (!variant?.compare_at_price || variant.compare_at_price <= variant.price) return '';

        const saveAmount = variant.compare_at_price - variant.price;

        if (this.saveTagType === 'percentage') {
          const percent = Math.round((saveAmount * 100) / variant.compare_at_price);
          return `Save ${percent}%`;
        }

        const formatted = window.Shopify?.formatMoney
          ? Shopify.formatMoney(saveAmount)
          : formatMoney(saveAmount);

        return `Save ${formatted.replace(/\.00$/, '')}`;
      }

      updateSaveTags() {
        if (!this.dataset.saveTagType) return;

        const selectedOptions = this.getSelectedOptions();

        this.querySelectorAll('fieldset').forEach((fieldset) => {
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            if (!label) return;

            const optionPosition = parseInt(input.dataset.optionPosition, 10);
            const variant = this.findVariantForOptionValue(optionPosition, input.value, selectedOptions);
            const saveTagText = variant ? this.formatSaveTagText(variant) : '';
            let saveTag = label.querySelector('.variant-save-tag');

            if (saveTagText) {
              if (!saveTag) {
                saveTag = document.createElement('span');
                saveTag.className = 'variant-save-tag';
                label.insertBefore(saveTag, label.firstChild);
              }

              saveTag.textContent = saveTagText;
              label.classList.add('variant-option-label--has-save-tag');
            } else {
              saveTag?.remove();
              label.classList.remove('variant-option-label--has-save-tag');
            }
          });
        });
      }

      updatePrice(variant) {
        const priceContainer = document.getElementById(this.priceId);
        if (!priceContainer) return;

        const priceEl = priceContainer.querySelector('.price-item--sale, .price-item--regular');
        const compareEl = priceContainer.querySelector('s.price-item--regular');

        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceContainer.classList.add('price--on-sale');
          if (compareEl) compareEl.textContent = formatMoney(variant.compare_at_price);
        } else {
          priceContainer.classList.remove('price--on-sale');
        }
      }

      updateMedia(variantId) {
        const mediaEntry = this.mediaMap.find((m) => m.id === variantId);
        const img = document.getElementById(this.mediaId);
        if (img && mediaEntry?.featured_media?.src) {
          img.src = mediaEntry.featured_media.src;
          img.alt = mediaEntry.featured_media.alt || '';
        }
      }
    }
  );
}

if (!customElements.get('cross-sell-modal-handler')) {
  customElements.define(
    'cross-sell-modal-handler',
    class CrossSellModalHandler extends HTMLElement {
      connectedCallback() {
        this.initModalHandler();
      }

      initModalHandler() {
        this.modal = document.getElementById(`CrossSellModal-${this.dataset.sectionId}`);
        if (!this.modal) {
          requestAnimationFrame(() => this.initModalHandler());
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        this.pendingProductForm = null;
        this.lastCartResponse = null;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this._origHide = this.modal.hide.bind(this.modal);

        this.modal.hide = () => {
          if (this.lastCartResponse) {
            this.finishWithCart();
            return;
          }
          this._origHide();
        };

        this.modal.querySelector('.cross-sell-modal__close')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.modal.hide();
        });

        this.modal.querySelectorAll('[data-action="skip"]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.modal.hide();
          });
        });

        this.modal.querySelector('[data-cross-sell-add-all]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleAddAllSelected();
        });

        this.modal.addEventListener('cross-sell:update', () => this.updateCrossSellTotal());
        this.initCrossSellSelectButtons();
        this.updateCrossSellTotal();
      }

      initCrossSellSelectButtons() {
        this.modal.querySelectorAll('[data-cross-sell-select], [data-cross-sell-select-media]').forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const card = trigger.closest('[data-cross-sell-product-card]');
            if (card) this.toggleCrossSellProductCard(card);
          });
        });

        this.modal.querySelectorAll('[data-cross-sell-select-media]').forEach((media) => {
          media.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            const card = media.closest('[data-cross-sell-product-card]');
            if (card) this.toggleCrossSellProductCard(card);
          });
        });
      }

      setCardSelected(card, isSelected) {
        const selectBtn = card.querySelector('[data-cross-sell-select]');
        const label = selectBtn?.querySelector('[data-cross-sell-select-label]');

        card.dataset.selected = isSelected ? 'true' : 'false';
        card.classList.toggle('cross-sell-modal__product--selected', isSelected);

        if (selectBtn) {
          selectBtn.classList.toggle('cross-sell-modal__select-btn--selected', isSelected);
          selectBtn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        }

        if (label) {
          label.textContent = isSelected ? 'Selected' : 'Select';
        }
      }

      toggleCrossSellProductCard(card) {
        const variantInput = card.querySelector('[data-cross-sell-variant-id]');
        if (variantInput?.disabled) return;
        const isSelected = card.dataset.selected === 'true';
        this.setCardSelected(card, !isSelected);
        this.updateCrossSellTotal();
      }

      updateCrossSellTotal() {
        const totalEl = this.modal.querySelector('[data-cross-sell-total]');
        const addAllBtn = this.modal.querySelector('[data-cross-sell-add-all]');
        if (!totalEl || !addAllBtn) return;

        let total = 0;
        let selectedCount = 0;

        this.modal.querySelectorAll('[data-cross-sell-product-card][data-selected="true"]').forEach((card) => {
          const variantInput = card.querySelector('[data-cross-sell-variant-id]');
          if (!variantInput || variantInput.disabled || !variantInput.value) return;
          total += parseInt(card.dataset.variantPrice || 0, 10);
          selectedCount += 1;
        });

        totalEl.textContent = formatMoney(total);
        addAllBtn.disabled = selectedCount === 0;
      }

      resetCrossSellSelections() {
        this.modal.querySelectorAll('[data-cross-sell-product-card]').forEach((card) => {
          this.setCardSelected(card, false);
        });
        this.updateCrossSellTotal();
      }

      getSelectedCrossSellItems() {
        const items = [];
        this.modal.querySelectorAll('[data-cross-sell-product-card][data-selected="true"]').forEach((card) => {
          const variantInput = card.querySelector('[data-cross-sell-variant-id]');
          if (!variantInput || variantInput.disabled || !variantInput.value) return;
          items.push({
            id: variantInput.value,
            quantity: 1,
            properties: { _source: CART_LINE_ITEM_SOURCE.crossSell },
          });
        });
        return items;
      }

      handleAddAllSelected() {
        const items = this.getSelectedCrossSellItems();
        if (!items.length) return;

        const productForm = this.pendingProductForm;
        const submitBtn = this.modal.querySelector('[data-cross-sell-add-all]');
        this.pendingProductForm = null;
        this.lastCartResponse = null;
        this._origHide();
        this.addItemsToCart(items, submitBtn, productForm);
      }

      showAfterMainAdded(productForm, cartResponse) {
        this.modal = document.getElementById(`CrossSellModal-${this.dataset.sectionId}`);
        if (!this.modal) return;
        if (!this.initialized) this.initModalHandler();

        this.pendingProductForm = productForm;
        this.lastCartResponse = cartResponse;
        this.resetMainButton(productForm);
        this.resetCrossSellSelections();

        requestAnimationFrame(() => {
          this.modal.show(productForm.submitButton);
        });
      }

      finishWithCart() {
        const productForm = this.pendingProductForm;
        const cartResponse = this.lastCartResponse;
        this.pendingProductForm = null;
        this.lastCartResponse = null;
        this._origHide();
        this.resetMainButton(productForm);

        if (cartResponse && this.cart) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            cartData: cartResponse,
          });
          this.cart.renderContents(cartResponse);
          if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        }
      }

      handleAddCrossSell(form) {
        const items = this.getFormItems(form);
        const productForm = this.pendingProductForm;
        const submitBtn = form?.querySelector('[type="submit"]');
        this.pendingProductForm = null;
        this.lastCartResponse = null;
        this._origHide();
        this.addItemsToCart(items, submitBtn, productForm);
      }

      resetMainButton(productForm) {
        if (!productForm?.submitButton) return;
        productForm.submitButton.classList.remove('loading');
        productForm.submitButton.removeAttribute('aria-disabled');
        productForm.submitButton.removeAttribute('disabled');
        productForm.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      setLoading(trigger, loading) {
        if (!trigger) return;
        if (loading) {
          trigger.setAttribute('aria-disabled', true);
          trigger.classList.add('loading');
          trigger.querySelector('.loading__spinner')?.classList.remove('hidden');
        } else {
          trigger.classList.remove('loading');
          trigger.removeAttribute('aria-disabled');
          trigger.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      getFormItems(form) {
        const formData = new FormData(form);
        return [
          {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity') || 1, 10),
            properties: { _source: CART_LINE_ITEM_SOURCE.crossSell },
          },
        ];
      }

      addItemsToCart(items, loadingTrigger, errorProductForm) {
        this.setLoading(loadingTrigger, true);

        const formData = new FormData();
        if (items.length === 1) {
          formData.append('id', items[0].id);
          formData.append('quantity', items[0].quantity);
          appendLineItemProperties(formData, items[0].properties);
        } else {
          items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            appendLineItemProperties(formData, item.properties, index);
          });
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (errorProductForm?.handleErrorMessage) {
                errorProductForm.handleErrorMessage(response.description);
              }
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              cartData: response,
            });

            this.cart.renderContents(response);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.setLoading(loadingTrigger, false);
            if (errorProductForm) this.resetMainButton(errorProductForm);
          });
      }
    }
  );
}

if (!customElements.get('product-upsell-slider')) {
  customElements.define(
    'product-upsell-slider',
    class ProductUpsellSlider extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.querySelectorAll('[data-upsell-add]').forEach((button) => {
          button.addEventListener('click', () => this.onUpsellClick(button));
        });

        this.bindUpsellModals();
        this.initSwiper();
      }

      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      initSwiper(attempt = 0) {
        const sliderEl = this.querySelector('.product-upsell__slider.swiper');
        const slides = sliderEl?.querySelectorAll('.swiper-slide') || [];

        if (!sliderEl || slides.length <= 1) return;

        if (typeof Swiper === 'undefined') {
          if (attempt < 20) requestAnimationFrame(() => this.initSwiper(attempt + 1));
          return;
        }

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        this.swiper = new Swiper(sliderEl, {
          slidesPerView: 1,
          spaceBetween: 15,
          loop: false,
          rewind: true,
          speed: 300,
          allowTouchMove: true,
          simulateTouch: true,
          grabCursor: true,
          touchRatio: 1,
          threshold: 8,
          longSwipesRatio: 0.25,
          navigation: {
            nextEl: this.querySelector('.product-upsell__nav-btn--next'),
            prevEl: this.querySelector('.product-upsell__nav-btn--prev'),
          },
        });
      }

      bindUpsellModals(attempt = 0) {
        const modals = document.querySelectorAll('[data-upsell-modals] .upsell-modal:not([data-upsell-bound])');

        if (!modals.length) {
          if (attempt < 20) requestAnimationFrame(() => this.bindUpsellModals(attempt + 1));
          return;
        }

        modals.forEach((modal) => {
          modal.dataset.upsellBound = 'true';
          this.initUpsellModal(modal);
        });
      }

      initUpsellModal(modal) {
        if (modal.parentElement !== document.body) {
          document.body.appendChild(modal);
        }

        const form = modal.querySelector('.upsell-modal__form');

        modal.addEventListener('click', (event) => {
          if (event.target === modal) modal.hide();
        });

        modal.querySelector('[data-upsell-close]')?.addEventListener('click', (event) => {
          event.preventDefault();
          modal.hide();
        });

        form?.addEventListener('submit', (event) => {
          event.preventDefault();
          const submitBtn = form.querySelector('[type="submit"]');
          this.addUpsellToCart(submitBtn, form, () => modal.hide());
        });
      }

      onUpsellClick(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        if (button.dataset.hasVariants === 'true') {
          const modal = document.getElementById(button.dataset.upsellModalId);
          if (modal?.show) modal.show(button);
          return;
        }

        this.addUpsellToCart(button);
      }

      addUpsellToCart(button, form = null, closeModal = null) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        button.setAttribute('aria-disabled', true);
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        const variantId = form ? new FormData(form).get('id') : button.dataset.variantId;

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        appendLineItemProperties(formData, { _source: CART_LINE_ITEM_SOURCE.upsell });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(button);
        }

        const config = fetchConfig('javascript');
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              cartData: response,
            });

            this.cart.renderContents(response);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (closeModal) closeModal();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}
